import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 200;
const DEACTIVATE_DELAY = 200;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
/** @type {WeakMap<HTMLElement, () => void>} */
const listTypeCustomCleanups = new WeakMap();

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          if (this.#state.activeItem && this.contains(/** @type {Node} */ (e.target))) {
            const item = this.#state.activeItem;
            this.#deactivate();
            item.focus();
            e.preventDefault();
          }
          return;
        }
        if (e.key !== 'Tab' || e.shiftKey) return;
        const target = e.target instanceof HTMLElement ? e.target : null;
        if (!target || target !== this.#state.activeItem) return;
        if (this.#state.activeItem?.getAttribute('aria-expanded') !== 'true') return;
        const submenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
        if (!submenu) return;
        const first =
          submenu.querySelector('.list-type-custom .menu-column-link') ||
          submenu.querySelector('a[href], button');
        if (first instanceof HTMLElement) {
          e.preventDefault();
          first.focus();
        }
      },
      { signal: this.#abortController.signal }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.querySelectorAll('[ref="submenu[]"]').forEach((el) => {
      if (el instanceof HTMLElement) delete el.dataset.active;
    });

    if (submenu) {
      submenu.dataset.active = '';
      if (submenu.querySelector('.list-type-custom')) {
        this.#initListTypeCustom(submenu, event);
      }
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * For list-type-custom mega menus only: open first children list by default and handle
   * .menu-column-link hover/focus and keyboard navigation for ADA.
   * Call only when submenu contains .list-type-custom; no-op for other list types.
   * @param {HTMLElement} submenu - Must contain .list-type-custom
   * @param {PointerEvent | FocusEvent} [event] - Activation event; if FocusEvent, focus first column link
   */
  #initListTypeCustom = (submenu, event) => {
    const listTypeCustomEl = submenu.querySelector('.list-type-custom');
    if (!listTypeCustomEl) return;
    const listTypeCustom = /** @type {HTMLElement} */ (listTypeCustomEl);

    const existingCleanup = listTypeCustomCleanups.get(submenu);
    if (existingCleanup) existingCleanup();

    const columnLinks = /** @type {HTMLElement[]} */ (
      Array.from(listTypeCustom.querySelectorAll('.menu-column-link'))
    );
    const linksWithChildren = columnLinks.filter((link) =>
      link.nextElementSibling?.classList?.contains('children-list')
    );

    /**
     * @param {HTMLElement | null} activeLink
     */
    const setActiveLink = (activeLink) => {
      columnLinks.forEach((link) => {
        link.classList.remove('is-active');
        if (link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
      });
      if (activeLink) {
        activeLink.classList.add('is-active');
        if (activeLink.hasAttribute('aria-expanded')) activeLink.setAttribute('aria-expanded', 'true');
      }
    };

    /** @returns {HTMLElement[]} */
    const getVisibleChildLinks = () => {
      const activeColumn = listTypeCustom.querySelector('.menu-column-link.is-active');
      const childrenList = activeColumn?.nextElementSibling;
      if (!childrenList?.classList?.contains('children-list')) return [];
      return /** @type {HTMLElement[]} */ (
        Array.from(childrenList.querySelectorAll('a.mega-menu__link'))
      );
    };

    const firstWithChildren = linksWithChildren[0];
    if (firstWithChildren) setActiveLink(firstWithChildren);

    /** @type {{ element: HTMLElement; type: string; handler: (e: Event) => void }[]} */
    const handlers = [];

    columnLinks.forEach((link) => {
      const hasChildren = link.nextElementSibling?.classList?.contains('children-list');

      const setActive = () => setActiveLink(hasChildren ? link : null);

      const mouseenterHandler = setActive;
      link.addEventListener('mouseenter', mouseenterHandler);
      handlers.push({ element: link, type: 'mouseenter', handler: mouseenterHandler });

      const focusinHandler = setActive;
      link.addEventListener('focusin', focusinHandler);
      handlers.push({ element: link, type: 'focusin', handler: focusinHandler });

      /** @param {KeyboardEvent} e */
      const keydownHandler = (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const idx = columnLinks.indexOf(link);
          const next = columnLinks[idx + 1];
          if (next) next.focus();
          else getVisibleChildLinks()[0]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const idx = columnLinks.indexOf(link);
          const prev = columnLinks[idx - 1];
          if (prev) prev.focus();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const childLinks = getVisibleChildLinks();
          const first = childLinks[0];
          if (first) first.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          columnLinks[0]?.focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          const last = columnLinks[columnLinks.length - 1];
          if (last) last.focus();
        }
      };
      link.addEventListener('keydown', keydownHandler);
      handlers.push({ element: link, type: 'keydown', handler: keydownHandler });
    });

    /** @param {KeyboardEvent} e */
    const childListKeydownHandler = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const childLinks = getVisibleChildLinks();
      if (!childLinks.length) return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (!target || !childLinks.includes(target)) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const activeColumn = listTypeCustom.querySelector('.menu-column-link.is-active');
        if (activeColumn instanceof HTMLElement) activeColumn.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = childLinks.indexOf(target);
        const next = childLinks[idx + 1];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = childLinks.indexOf(target);
        const prev = childLinks[idx - 1];
        if (prev) prev.focus();
        else {
          const activeColumn = listTypeCustom.querySelector('.menu-column-link.is-active');
          if (activeColumn instanceof HTMLElement) activeColumn.focus();
        }
      }
    };
    listTypeCustom.addEventListener('keydown', childListKeydownHandler);
    handlers.push({ element: listTypeCustom, type: 'keydown', handler: childListKeydownHandler });

    const cleanup = () => {
      handlers.forEach(({ element, type, handler }) =>
        element.removeEventListener(type, handler)
      );
      setActiveLink(null);
      listTypeCustomCleanups.delete(submenu);
    };
    listTypeCustomCleanups.set(submenu, cleanup);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item !== this.#state.activeItem) return;

    const submenu = this.#state.activeItem ? findSubmenu(this.#state.activeItem) : null;
    if (
      submenu &&
      event.relatedTarget instanceof Node &&
      submenu.contains(event.relatedTarget)
    ) {
      return;
    }

    this.#debouncedDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const listTypeCleanup = submenu ? listTypeCustomCleanups.get(submenu) : undefined;
    if (listTypeCleanup) listTypeCleanup();

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
